import { CSSProperties, FormEvent, useState } from 'react';
import { supabase } from '../lib/supabase';
import { BrandGlyph, MODULE_COLOR } from '../lib/branding';

/* ============================================================
   NativeSignIn
   ------------------------------------------------------------
   In-app email/password sign-in used by the native iOS shell
   (Capacitor build). Replaces the web PWA's "bounce to
   plan.cadtomie.com" flow because:
     1. App Store reviewers reject native apps that send users
        to the system browser for primary auth (4.0 — Design).
     2. The cross-subdomain cookie trick the planner+intraop
        share doesn't reach into `capacitor://localhost`, so a
        round-trip wouldn't even produce a session here.
     3. cookieStorage.ts already falls back to localStorage on
        non-cadtomie hostnames — Supabase persists the session
        in WKWebView's localStorage, scoped per app, surviving
        relaunches.

   Sign-up, password reset, and any future social/OAuth flows
   stay on plan.cadtomie.com — surfaced as a small hint, not a
   primary action. Apple is fine with that; they only require
   that the LOGIN itself happen in-app.
   ============================================================ */

export function NativeSignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (authError) throw authError;
      // On success, useSession's listener fires and RequireSession
      // re-renders with the gated children.
    } catch (err) {
      // Supabase errors carry a `message` field we can show as-is.
      // For common cases ("Invalid login credentials") this is readable
      // enough; we don't try to translate.
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="safe-top safe-bottom" style={pageStyle}>
      <Atmosphere />
      <form style={cardStyle} onSubmit={handleSubmit} noValidate>
        <BrandGlyph size={48} color={MODULE_COLOR.angle} />
        <h1 style={titleStyle}>Sign in to Intraop</h1>
        <p style={subtitleStyle}>
          One CADtomie account covers both the planner and Intraop.
        </p>

        <label style={fieldLabelStyle}>
          <span className="sr-only">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={fieldLabelStyle}>
          <span className="sr-only">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>

        {error && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          style={primaryBtnStyle}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p style={hintStyle}>
          New here? Create an account at{' '}
          <strong style={{ color: 'var(--text)' }}>plan.cadtomie.com</strong>{' '}
          — then come back and sign in here.
        </p>
      </form>
    </main>
  );
}

/* ─────────────────── Background atmosphere ─────────────────── */

function Atmosphere() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(60% 40% at 50% 0%, rgba(124,196,255,0.08), transparent 60%), ' +
          'radial-gradient(70% 50% at 50% 100%, rgba(124,196,255,0.04), transparent 60%)',
      }}
    />
  );
}

/* ─────────────────── Styles ─────────────────── */

const pageStyle: CSSProperties = {
  position: 'relative',
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: 'var(--bg)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  maxWidth: 420,
  width: '100%',
  background: 'var(--surface-strong)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-lg)',
  padding: '36px 28px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 14,
  textAlign: 'center',
  boxShadow: 'var(--shadow)',
};

const titleStyle: CSSProperties = {
  margin: '8px 0 0 0',
  color: 'var(--text)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.02em',
};

const subtitleStyle: CSSProperties = {
  margin: '0 0 8px 0',
  color: 'var(--text-dim)',
  fontSize: 15,
  lineHeight: 1.5,
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  textAlign: 'left',
};

// font-size: 16px is the iOS Safari/WKWebView threshold below which the
// keyboard zoom-in kicks in on focus. Keep at >=16 to avoid the layout jump.
const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  padding: '14px 16px',
  fontSize: 16,
  lineHeight: 1.3,
  outline: 'none',
  // Disables iOS's blue glow on focus while keeping a visible focus ring
  // for keyboard users. The outline rule below provides accessibility.
  WebkitTapHighlightColor: 'transparent',
  font: 'inherit',
};

const errorStyle: CSSProperties = {
  margin: 0,
  color: 'var(--danger)',
  fontSize: 13,
  textAlign: 'left',
  lineHeight: 1.4,
};

const primaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: 4,
  background: 'var(--accent)',
  color: '#0a0a0a',
  border: 'none',
  borderRadius: 999,
  padding: '14px 24px',
  minHeight: 'var(--tap-pref)', // 64px — gloved-finger-friendly
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  font: 'inherit',
};

const hintStyle: CSSProperties = {
  margin: '4px 0 0 0',
  color: 'var(--text-muted)',
  fontSize: 12,
  lineHeight: 1.45,
  textAlign: 'center',
};
